// Text-to-speech via the browser Web Speech API (free, on-device, RU/EN).
import type { Lang } from "./i18n";

export function ttsSupported(): boolean {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

// Some browsers load voices asynchronously — warm them up once.
export function warmUpVoices(): void {
  if (!ttsSupported()) return;
  try {
    window.speechSynthesis.getVoices();
    window.speechSynthesis.onvoiceschanged = () => window.speechSynthesis.getVoices();
  } catch {
    /* ignore */
  }
}

function pickVoice(prefix: string): SpeechSynthesisVoice | undefined {
  try {
    return window.speechSynthesis
      .getVoices()
      .find((v) => v.lang?.toLowerCase().startsWith(prefix));
  } catch {
    return undefined;
  }
}

// Chrome stops long speech after ~15s; pause()/resume() keeps it alive.
let keepAlive: number | undefined;
function startKeepAlive() {
  stopKeepAlive();
  keepAlive = window.setInterval(() => {
    const s = window.speechSynthesis;
    if (s.speaking) {
      s.pause();
      s.resume();
    } else {
      stopKeepAlive();
    }
  }, 9000);
}
function stopKeepAlive() {
  if (keepAlive !== undefined) {
    clearInterval(keepAlive);
    keepAlive = undefined;
  }
}

/** Speak an ordered list of text blocks. Splits long blocks into sentences so
 *  nothing gets truncated. `onDone` fires after the last sentence. */
export function speak(texts: string[], lang: Lang, onDone: () => void): void {
  if (!ttsSupported()) {
    onDone();
    return;
  }
  const synth = window.speechSynthesis;
  synth.cancel();
  const CODES: Record<Lang, string> = { ru: "ru-RU", en: "en-US", ps: "ps-AF", fa: "fa-IR" };
  const code = CODES[lang] || "en-US";
  // ps/fa voices are rarely installed — pickVoice returns undefined and the
  // engine falls back (Farsi may speak on some devices; Pashto usually silent).
  const voice = pickVoice(lang);

  const sentences = texts
    .flatMap((t) => t.split(/(?<=[.!?…])\s+/))
    .map((s) => s.trim())
    .filter(Boolean);

  if (!sentences.length) {
    onDone();
    return;
  }

  sentences.forEach((s, i) => {
    const u = new SpeechSynthesisUtterance(s);
    u.lang = code;
    if (voice) u.voice = voice;
    u.rate = 1;
    if (i === sentences.length - 1) {
      u.onend = () => {
        stopKeepAlive();
        onDone();
      };
      u.onerror = () => {
        stopKeepAlive();
        onDone();
      };
    }
    synth.speak(u);
  });
  startKeepAlive();
}

export function stopSpeak(): void {
  if (!ttsSupported()) return;
  stopKeepAlive();
  try {
    window.speechSynthesis.cancel();
  } catch {
    /* ignore */
  }
}
